import { observer, inject } from 'mobx-react'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'

import Header from '@client/components/Header'
import FooterNav from '@client/components/FooterNav'
import Preview from '@client/components/Preview'
import Sidebar from '@client/components/Sidebar'
import Editor from '@client/components/Editor'
import AutoSave from '@client/utils/Autosave'
import documentSync from '@client/utils/DocumentSync'
import { SaveAsTemplateDialog } from '@client/components/SaveAsTemplateDialog'
import { SaveAsBlockDialog } from '@client/components/SaveAsBlockDialog'
import { RemoveBlockDialog } from '@client/components/RemoveBlockDialog'
import FileManager from '@client/components/FileManager'
import ganApi from '@client/api/gan'
import RequiredLinksWarning from '@client/components/RequiredLinksWarning'
import LinkIssues from '@client/components/LinkIssues'
import RequiredBlocksWarning from '@client/components/RequiredBlocksWarning'
import StorageQuotaWarning from '@client/components/StorageQuotaWarning'
import RemoteChangeWarning from '@client/components/RemoteChangeWarning'
import SaveErrorBanner from '@client/components/SaveErrorBanner'
import RemoteChangeToast from '@client/components/RemoteChangeToast'
import SaveConflictDialog from '@client/components/SaveConflictDialog'
import { VIEW_TYPES } from '@client/components/constants'
import AppModel, { NULL as AppModelNULL } from './model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import ErrorDialog from '@client/components/ErrorDialog/component'
import { pageviewTrack } from '@client/utils/googleTagManager'
import { update as intercomUpdate } from '@client/utils/intercom'
import { setUserId, clearUser } from '@client/utils/sentry'
import { isStaffImpersonating } from '@client/utils/impersonation'
import { isLeavingOnPurpose } from '@client/utils/pageUnload'

import './styles.scss'

@inject('app', 'editor')
@observer
class App extends Component {
  static propTypes = {
    app: PropTypes.instanceOf(AppModel),
    editor: PropTypes.instanceOf(EditorModel),
  }

  static defaultProps = {
    app: AppModelNULL,
    editor: EditorModelNULL,
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onWindowUnload)
    window.removeEventListener('mousedown', this.checkUserInteracted)
    window.removeEventListener('touchstart', this.checkUserInteracted)
    window.removeEventListener('scroll', this.checkUserInteracted)
    window.removeEventListener('keydown', this.checkUserInteracted)
    AutoSave.unBind()
    documentSync.unBind()
  }

  // Edits that only exist in this tab: unsaved changes, a save that failed
  // (FE-1431), or one blocked by a newer version (FE-1441).
  hasUnsavedWork = () => {
    const { editor } = this.props
    return (
      editor.hasUnsavedUserChanges() ||
      editor.lastSaveFailed ||
      !!editor.saveConflict
    )
  }

  // The browser only acts on what this handler does synchronously, so ask
  // before leaving right away and start the save without waiting for it. If
  // the user stays, the save completes as usual. If they leave, the browser
  // cancels it; the local copy from storage.saveItem is all that's kept, so
  // the prompt is what protects their work. Not when the app itself leaves
  // after explaining what happens to the edits (allowLeaving). See FE-1454.
  onWindowUnload = (e) => {
    if (isLeavingOnPurpose() || !this.hasUnsavedWork()) {
      return
    }
    e.preventDefault()
    e.returnValue = ''
    this.props.editor.save()
  }

  async componentDidMount() {
    const profile = await ganApi.getUserProfile()
    if (profile && profile.timezone) {
      moment.tz.setDefault(profile.timezone)
    } else {
      moment.tz.setDefault(moment.tz.guess(true))
    }

    let user = this.props.app.user
    if (!user) {
      user = await ganApi.getUser()
      this.props.app.setUser(user)
    }

    // v4/user also carries the personal UI language (FE-1259), needed
    // regardless of impersonation, so fetch it unconditionally rather than
    // only inside the identity branch below. getUserV4() resolves to
    // undefined (rather than rejecting) on a fetch failure, so guard against
    // that everywhere it's used.
    const v4User = await ganApi.getUserV4()

    // Suppress customer identification when a staff member is impersonating
    // via "Login as user" — otherwise Sentry, Google Analytics and Intercom are
    // attributed to the impersonated customer. Route every identity/analytics
    // sink through this one guard so none can silently bypass it. Reset any
    // stale Sentry identity too. See FE-1138.
    if (isStaffImpersonating()) {
      clearUser()
    } else {
      // Sentry and Intercom both key on the v4 user `id` (Profile.hash), not
      // the integer PK, to avoid collisions between the Profile/Account PK
      // sequences. See FE-1106 and FE-1209.
      if (v4User) {
        setUserId(v4User.id)
        intercomUpdate(v4User)
      }
      pageviewTrack(
        {
          profile,
          user,
        },
        window.location
      )
    }

    this.props.app.setProfile(profile, v4User?.language)

    // Ask before leaving with unsaved work
    window.addEventListener('beforeunload', this.onWindowUnload)

    window.addEventListener('mousedown', this.checkUserInteracted)
    window.addEventListener('touchstart', this.checkUserInteracted)
    window.addEventListener('scroll', this.checkUserInteracted)
    window.addEventListener('keydown', this.checkUserInteracted)
  }

  checkUserInteracted = (evt) => {
    this.props.editor.markUserActivity()
  }

  /**
   * For future use
   *
   * @returns
   * @memberof App
   */
  getLocationFromBrowser() {
    const language =
      navigator.language ||
      (navigator.languages && navigator.languages[0]) ||
      navigator.language ||
      navigator.userLanguage

    return language.toLowerCase().split(/[_-]+/)[0]
  }

  render() {
    const { view } = this.props.app

    return (
      <div
        className={`app-container${
          this.props.editor.linkIssuesPanelOpen
            ? ' app-container--issues-open'
            : ''
        }`}
        data-testid="app-container"
      >
        <If
          condition={
            view === VIEW_TYPES.EDITOR ||
            view === VIEW_TYPES.FILE_MANAGER_FROM_EDITOR ||
            view === VIEW_TYPES.FILE_MANAGER_FROM_SIDEBAR
          }
        >
          <Header />
          <Sidebar />
          <LinkIssues />
          <If condition={this.props.app.hasWizard}>
            <FooterNav
              app={this.props.app}
              stepIndex={this.props.app.stepIndex}
              wizard={this.props.app.wizard}
            />
          </If>
          <RequiredLinksWarning />
          <RequiredBlocksWarning />
          <StorageQuotaWarning />
          <RemoteChangeWarning />
          <SaveErrorBanner />
          <Editor />
          <Preview />
          <SaveAsTemplateDialog />
          <SaveAsBlockDialog />
          <RemoveBlockDialog />
          <FileManager />
          <ErrorDialog app={this.props.app} editor={this.props.editor} />
          <RemoteChangeToast />
          <SaveConflictDialog />
        </If>
      </div>
    )
  }
}

export default App
